import { Component, computed, input } from '@angular/core';

import { IContentBuilderFieldImage } from '@core/models/content-builder-fields.model';
import {
  imageFullWidthBlockResolveConfig,
  type ImageFullWidthBlockConfig,
} from '@front-platform/content-builder';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';

export interface ImageFullWidthBlockRuntimeConfig {
  image?: IContentBuilderFieldImage;
  display_kiosk?: boolean;
  use_container?: boolean;
  height_property?: 'pixel' | 'percent' | 'none';
  height?: number;
  padding_top?: string;
  padding_bottom?: string;
  link_url?: string;
  link_target?: string;
}

export type ImageFullWidthBlockConfigInput = Partial<
  Omit<ImageFullWidthBlockConfig, keyof ImageFullWidthBlockRuntimeConfig> &
    ImageFullWidthBlockRuntimeConfig
>;
export type ImageFullWidthBlockResolvedConfig = Omit<
  ImageFullWidthBlockConfig,
  keyof ImageFullWidthBlockRuntimeConfig
> &
  Partial<ImageFullWidthBlockRuntimeConfig>;
export interface ImageFullWidthBlockContract extends ConfigComponentContract<
  ImageFullWidthBlockConfigInput,
  ImageFullWidthBlockResolvedConfig
> {}

@Component({
  selector: 'app-image-full-width-block',
  templateUrl: './image-full-width-block.component.html',
  standalone: false,
})
export class ImageFullWidthBlockComponent implements ImageFullWidthBlockContract {
  readonly config = input<ImageFullWidthBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ImageFullWidthBlockConfig,
    ImageFullWidthBlockRuntimeConfig
  >(imageFullWidthBlockResolveConfig, () => ({ config: this.config() }));

  get image(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig().image as unknown as IContentBuilderFieldImage | undefined;
  }

  get displayKiosk(): boolean {
    return this.resolvedConfig().display_kiosk ?? false;
  }

  /** When false, skip container + padding classes (full-bleed). Missing key → true (legacy). */
  get useContainer(): boolean {
    return this.resolvedConfig().use_container !== false;
  }

  get paddingTop(): string {
    return this.resolvedConfig().padding_top ?? 'none';
  }

  get paddingBottom(): string {
    return this.resolvedConfig().padding_bottom ?? 'small';
  }

  get linkUrl(): string | undefined {
    return this.resolvedConfig().link_url || undefined;
  }

  get linkTarget(): string {
    return this.resolvedConfig().link_target ?? '_self';
  }

  protected readonly heightStyles = computed(() => {
    const property = this.resolvedConfig().height_property ?? 'none';
    if (property === 'none') return {};
    return {
      height: `${this.resolvedConfig().height ?? 100}${property === 'percent' ? 'vh' : 'px'}`,
      'object-fit': 'cover',
    };
  });
}

COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ImageFullWidthBlockComponent, {});
